import axios from "axios";
import { auth } from "../config/firebase";
import { signInAnonymously } from "firebase/auth";

const API_URL = import.meta.env.VITE_API_URL
const MASJID_API = import.meta.env.VITE_MASJID_API
const PRAYER_API = import.meta.env.VITE_PRAYER_API

export const MASJID_API_BASE = MASJID_API;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

// Masjid API instance (separate base)
export const masjidApi = axios.create({
  baseURL: MASJID_API,
  timeout: 120000, // 2 minutes for image uploads
});

// Add auth token to requests with retry logic
api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    try {
      // Get fresh token
      const token = await user.getIdToken(false);
      config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.error("Error getting auth token:", error);
    }
  }
  return config;
});

masjidApi.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken(false);
      // Ensure headers object exists
      if (!config.headers) {
        config.headers = {};
      }
      config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.error("Error getting auth token:", error);
    }
  }
  return config;
});

// Add response interceptor to handle 401 errors with token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If we get a 401 and haven't retried yet, refresh the token and retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const user = auth.currentUser;
      if (user) {
        try {
          // Force refresh the token
          const token = await user.getIdToken(true);
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } catch (refreshError) {
          console.error("Error refreshing auth token:", refreshError);
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// Public APIs (No Auth Required)
// ============================================================================

/**
 * Get nearby masjids with prayer times
 * @param {number} lat - Latitude
 * @param {number} long - Longitude
 * @param {string} date - Optional date in YYYY-MM-DD format
 */
export const getNearbyMasjids = async (lat, long, date = null) => {
  const params = { lat, long };
  if (date) params.date = date;
  const response = await api.get("/masjids", { params });
  return response.data;
};

/**
 * List available prayer-time months for a masjid (public)
 * @param {string} masjidId - Masjid ID
 */
export const getPublicPrayerMonths = async (masjidId) => {
  const response = await api.get(
    `/v1/public/masjid/${encodeURIComponent(masjidId)}/prayer-times/months`
  );
  return response.data;
};

/**
 * Get prayer times for a masjid/month (public)
 * @param {string} masjidId - Masjid ID
 * @param {string} yearMonth - YYYY-MM key
 */
export const getPublicMonthlyPrayerTimes = async (masjidId, yearMonth) => {
  const response = await api.get(
    `/v1/public/masjid/${encodeURIComponent(masjidId)}/prayer-times/${yearMonth}`
  );
  return response.data;
};

/**
 * Get prayer times for coordinates
 * @param {number} latitude - Latitude
 * @param {number} longitude - Longitude
 */
export const getPrayerTimes = async (latitude, longitude) => {
  try {
    const response = await axios.get(PRAYER_API, {
      params: { latitude, longitude, method: "ISNA" },
      timeout: 10000,
    });
    return response.data;
  } catch (error) {
    console.error("Prayer times API error:", error);
    throw error;
  }
};

/**
 * Check if email is an admin
 * @param {string} email - Email to check
 */
export const checkAdmin = async (email) => {
  const response = await api.get("/check-admin", { params: { email } });
  return response.data;
};

/**
 * Get app version
 */
export const getAppVersion = async () => {
  const response = await api.get("/version");
  return response.data;
};

/**
 * Get global feature flags (public). Currently: { halalEnabled }
 */
export const getFeatureFlags = async () => {
  const response = await api.get("/v1/config/features");
  return response.data;
};

/**
 * Get masjid details with prayer times for a specific date (public)
 * @param {string} masjidId - Masjid ID
 * @param {string} date - Optional date in YYYY-MM-DD format
 */
export const getMasjidWithPrayerTimes = async (masjidId, date = null) => {
  const params = date ? { date } : {};
  const response = await api.get(`/masjid/${masjidId}`, { params });
  return response.data;
};

// ============================================================================
// Masjid Management APIs (Admin Only)
// ============================================================================

/**
 * Update masjid information
 * @param {string} masjidId - Masjid ID
 * @param {object} updates - Fields to update
 */
export const updateMasjid = async (masjidId, updates) => {
  const response = await api.put(`/v1/masjid/${masjidId}`, { updates });
  return response.data;
};

/**
 * Refresh prayer times for a masjid from Masjidal.com
 * @param {string} masjidId - Masjid ID
 */
export const refreshMasjidPrayerTimes = async (masjidId) => {
  const response = await api.post(`/v1/masjid/${masjidId}/refresh-times`);
  return response.data;
};

/**
 * Refresh Google Calendar events for a masjid
 * @param {string} masjidId - Masjid ID
 * @param {number} days - Number of days to fetch (default 60)
 */
export const refreshGoogleCalendarEvents = async (masjidId, days = 60) => {
  const response = await api.post(
    `/v1/masjid/${masjidId}/refresh-gcal?days=${days}`
  );
  return response.data;
};

// ============================================================================
// Event Management APIs (Admin Only)
// ============================================================================

/**
 * Create a new event
 * @param {string} masjidId - Masjid ID
 * @param {object} eventData - Event details
 */
export const createEvent = async (masjidId, eventData) => {
  const response = await eventsApi.post(`/v1/events`, { ...eventData, masjidId });
  return response.data;
};

/**
 * Update an existing event
 * @param {string} eventId - Event ID
 * @param {object} eventData - Updated event details
 * @param {string} scope - Edit scope: "single" or "series" (default: "single")
 */
export const updateEvent = async (eventId, eventData, scope = "single") => {
  const response = await eventsApi.put(`/v1/events/${eventId}`, {
    ...eventData,
    scope,
  });
  return response.data;
};

/**
 * Delete an event
 * @param {string} eventId - Event ID
 */
export const deleteEvent = async (eventId, scope = "single") => {
  const response = await eventsApi.delete(`/v1/events/${eventId}`, {
    params: { scope },
    data: { scope },
  });
  return response.data;
};

// ============================================================================
// Google Calendar Management APIs (Admin Only)
// ============================================================================

/**
 * Get list of Google Calendars for a masjid
 * @param {string} masjidId - Masjid ID
 */
export const getGoogleCalendars = async (masjidId) => {
  const response = await api.get(`/v1/masjid/${masjidId}/gcal`);
  return response.data;
};

/**
 * Add a Google Calendar to a masjid
 * @param {string} masjidId - Masjid ID
 * @param {string} calendarId - Google Calendar ID
 * @param {object} settings - Optional settings (timeOffset, nickname, filters)
 */
export const addGoogleCalendar = async (masjidId, calendarId, settings = {}) => {
  const response = await api.post(`/v1/masjid/${masjidId}/gcal`, {
    calendarId,
    ...settings,
  });
  return response.data;
};

/**
 * Update Google Calendar settings
 * @param {string} masjidId - Masjid ID
 * @param {string} calendarId - Google Calendar ID
 * @param {object} settings - Settings (timeOffset, nickname, filters)
 */
export const updateGoogleCalendar = async (masjidId, calendarId, settings) => {
  const response = await api.post(`/v1/masjid/${masjidId}/gcal`, {
    calendarId,
    ...settings,
  });
  return response.data;
};

/**
 * Remove a Google Calendar from a masjid
 * @param {string} masjidId - Masjid ID
 * @param {string} calendarId - Google Calendar ID
 */
export const removeGoogleCalendar = async (masjidId, calendarId) => {
  const response = await api.delete(
    `/v1/masjid/${masjidId}/gcal/${encodeURIComponent(calendarId)}`
  );
  return response.data;
};

// ============================================================================
// Admin Management APIs (Admin Owner Only)
// ============================================================================

/**
 * Get all admins for a masjid
 * @param {string} masjidId - Masjid ID
 */
export const getMasjidAdmins = async (masjidId) => {
  const response = await api.get(`/v1/masjid/${masjidId}/admins`);
  return response.data;
};

/**
 * Add an admin to a masjid
 * @param {string} masjidId - Masjid ID
 * @param {string} email - Admin email
 * @param {string} role - Role (Admin or Admin Owner)
 */
export const addMasjidAdmin = async (masjidId, email, role = "Admin") => {
  const response = await api.post(`/v1/masjid/${masjidId}/admins`, {
    email,
    role,
  });
  return response.data;
};

/**
 * Remove an admin from a masjid
 * @param {string} masjidId - Masjid ID
 * @param {string} email - Admin email
 */
export const removeMasjidAdmin = async (masjidId, email) => {
  const response = await api.delete(`/v1/masjid/${masjidId}/admins/${email}`);
  return response.data;
};

// ============================================================================
// Super Admin APIs
// ============================================================================

/**
 * Set active masjid for super admin
 * @param {string} masjidId - Masjid ID to set as active
 */
export const setActiveMasjid = async (masjidId) => {
  const response = await api.post("/v1/super-admin/set-active-masjid", {
    masjidId,
  });
  return response.data;
};

/**
 * Search masjids for super admin dropdown
 * @param {string} query - Search query (min 2 chars)
 * @param {number} limit - Max results (default 15)
 */
export const searchMasjids = async (query, limit = 15) => {
  const response = await api.get("/v1/admin/masjids/search", {
    params: { q: query, limit },
  });
  return response.data;
};

// ============================================================================
// Notification APIs (Admin Only)
// ============================================================================

/**
 * Send notification to masjid subscribers
 * @param {string} masjidId - Masjid ID
 * @param {string} body - Notification message
 */
export const sendMasjidNotification = async (masjidId, body) => {
  const response = await api.post(`/v1/masjid/${masjidId}/send-notification`, {
    body,
  });
  return response.data;
};

// ============================================================================
// Account Management
// ============================================================================

/**
 * Update user password
 * @param {string} currentPassword - Existing password
 * @param {string} newPassword - New password to set
 */
export const changePassword = async (currentPassword, newPassword) => {
  const response = await api.post("/v1/account/change-password", {
    currentPassword,
    newPassword,
  });
  return response.data;
};

/**
 * Delete user account
 */
export const deleteAccount = async () => {
  const response = await api.delete("/v1/account");
  return response.data;
};

// ============================================================================
// TeleView APIs
// ============================================================================

/**
 * List TeleView images metadata for cache sync (no signed URLs, minimal reads)
 * Returns: storagePath, md5Hash, name, uploadedAt
 * @param {string} masjidId - Masjid ID
 */
export const getTeleviewImagesMetadata = async (masjidId) => {
  const response = await masjidApi.get(
    `/v1/masjid/${encodeURIComponent(masjidId)}/teleview/images/metadata`
  );
  return response.data || {};
};

/**
 * List TeleView images for a masjid (secured, server-side Storage access)
 * @param {string} masjidId - Masjid ID
 */
export const getTeleviewImages = async (masjidId) => {
  const response = await masjidApi.get(
    `/v1/masjid/${encodeURIComponent(masjidId)}/teleview/images`
  );
  const data = response.data || {};
  if (data?.ok && Array.isArray(data.images)) {
    const base = (MASJID_API || "").replace(/\/$/, "");
    const images = data.images.map((img) => {
      const storageUrl = img.url;
      const downloadUrl = img.downloadPath ? `${base}${img.downloadPath}` : storageUrl;
      return { ...img, storageUrl, url: downloadUrl, downloadUrl };
    });
    return { ...data, images };
  }
  return data;
};

/**
 * Get signed upload URL for TeleView image
 * @param {string} masjidId
 * @param {string} filename
 * @param {string} contentType
 */
export const getTeleviewUploadUrl = async (masjidId, filename, contentType) => {
  const response = await masjidApi.post(
    `/v1/masjid/${encodeURIComponent(masjidId)}/teleview/images/upload-url`,
    { filename, contentType }
  );
  return response.data;
};

/**
 * Delete a TeleView image via backend (secured)
 * @param {string} masjidId
 * @param {string} storagePath - The storage path of the image to delete
 */
export const deleteTeleviewImage = async (masjidId, storagePath) => {
  const response = await masjidApi.delete(
    `/v1/masjid/${encodeURIComponent(masjidId)}/teleview/images`,
    { params: { path: storagePath } }
  );
  const data = response.data;
  if (!data?.ok) {
    const msg = data?.error || "Delete failed";
    const err = new Error(msg);
    err.responseData = data;
    throw err;
  }
  return data;
};

/**
 * Upload TeleView image via backend (avoids Storage CORS)
 * @param {string} masjidId
 * @param {File} file
 */
export const uploadTeleviewImage = async (masjidId, file) => {
  const formData = new FormData();
  formData.append("file", file, file.name);

  const response = await masjidApi.post(
    `/v1/masjid/${encodeURIComponent(masjidId)}/teleview/images/upload`,
    formData,
    {
      // DO NOT set Content-Type header manually!
      // Axios must set it automatically with the proper boundary parameter
      timeout: 180000, // 3 minutes for individual uploads
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`Upload progress: ${percentCompleted}%`);
      },
    }
  );
  const data = response.data;
  if (!data?.ok) {
    const msg = data?.error || "Upload failed";
    const err = new Error(msg);
    err.responseData = data;
    throw err;
  }
  return data;
};

/**
 * Get all TV display data in one call (masjid info, today's prayer times, tomorrow's prayer times)
 * @param {string} masjidId - Masjid ID
 * @returns {Promise<{ok: boolean, masjid: object, todayPrayerTimes: object, tomorrowPrayerTimes: object}>}
 */
export const getTvData = async (masjidId) => {
  const response = await masjidApi.get(
    `/v1/tv/data/${encodeURIComponent(masjidId)}`
  );
  return response.data;
};

/**
 * Get events for a masjid (legacy)
 * @param {string} masjidId - Masjid ID
 * @returns {Promise<{ok: boolean, events: Array}>}
 */
export const getEvents = async (masjidId) => {
  const response = await masjidApi.get("/events", {
    params: { masjidId },
  });
  return response.data;
};

// ============================================================================
// Event Service APIs (New Dedicated Event Endpoints)
// ============================================================================

const EVENTS_API = import.meta.env.VITE_EVENTS_API || import.meta.env.VITE_API_URL;

// Events API instance (with auth)
export const eventsApi = axios.create({
  baseURL: EVENTS_API,
  timeout: 30000,
});

// Public Events API instance (NO auth required - for public endpoints like share links)
export const publicEventsApi = axios.create({
  baseURL: EVENTS_API,
  timeout: 30000,
});

// Track if anonymous auth is in progress to prevent multiple attempts
let anonAuthPromise = null;

/**
 * Ensure we have an authenticated user (anonymous if not logged in)
 * This is required because the events API requires authentication
 */
async function ensureAuth() {
  // If user is already signed in (regular or anonymous), return them
  if (auth.currentUser) {
    return auth.currentUser;
  }

  // If anonymous auth is already in progress, wait for it
  if (anonAuthPromise) {
    return anonAuthPromise;
  }

  // Sign in anonymously
  try {
    anonAuthPromise = signInAnonymously(auth);
    const result = await anonAuthPromise;
    anonAuthPromise = null;
    return result.user;
  } catch (error) {
    anonAuthPromise = null;
    console.error("Anonymous auth failed:", error.code, error.message);
    return null;
  }
}

// Add auth interceptor - ensures auth token is present for authenticated requests
eventsApi.interceptors.request.use(async (config) => {
  // Skip auth for public endpoints - they use optionalAuth on backend
  const isPublicEndpoint = config.url?.includes('/v1/public/');
  
  if (isPublicEndpoint) {
    // For public endpoints, only add token if user is already logged in
    if (auth.currentUser) {
      try {
        const token = await auth.currentUser.getIdToken(false);
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        // Ignore token errors for public endpoints
      }
    }
    return config;
  }
  
  // For non-public endpoints, ensure we have an authenticated user
  const user = await ensureAuth();
  
  if (user) {
    try {
      const token = await user.getIdToken(false);
      config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.error("Error getting auth token:", error);
    }
  }
  return config;
});

eventsApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      // Try to get a fresh token (may trigger anonymous auth if needed)
      const user = await ensureAuth();
      if (user) {
        try {
          const token = await user.getIdToken(true);
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return eventsApi(originalRequest);
        } catch (refreshError) {
          console.error("Error refreshing auth token:", refreshError);
          return Promise.reject(refreshError);
        }
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Get public upcoming events
 * @param {object} options - Query options
 * @param {number} options.lat - User latitude for distance filtering
 * @param {number} options.lng - User longitude for distance filtering
 * @param {number} options.radius - Filter radius in miles (default 50)
 * @param {number} options.days - Number of days to fetch (default 60)
 * @param {string} options.masjidId - Filter by specific masjid
 * @param {number} options.limit - Max events to return (default 100)
 * @param {string} options.q - Free-text search query (title, description, location, masjid)
 * @param {AbortSignal} options.signal - Optional signal to cancel an in-flight request
 */
export const getPublicEvents = async (options = {}) => {
  const { lat, lng, radius, days, masjidId, limit, q, signal } = options;
  const params = {};
  if (lat != null) params.lat = lat;
  if (lng != null) params.lng = lng;
  if (radius != null) params.radius = radius;
  if (days != null) params.days = days;
  if (masjidId) params.masjidId = masjidId;
  if (limit != null) params.limit = limit;
  if (q) params.q = q;

  const response = await eventsApi.get("/v1/public/events", { params, signal });
  return response.data;
};

/**
 * Get a single public event by ID
 * @param {string} eventId - Event ID
 * @param {string} date - Optional date for recurring event instance (YYYY-MM-DD)
 */
export const getPublicEvent = async (eventId, date = null) => {
  const params = {};
  if (date) params.date = date;
  const response = await eventsApi.get(`/v1/public/events/${eventId}`, { params });
  return response.data;
};

/**
 * Get events for a masjid (admin view)
 * @param {string} masjidId - Masjid ID
 * @param {object} options - Query options
 * @param {boolean} options.includePast - Include past events
 * @param {number} options.days - Number of days to fetch
 */
export const getMasjidEvents = async (masjidId, options = {}) => {
  const { includePast, days } = options;
  const params = {};
  if (includePast != null) params.includePast = includePast;
  if (days != null) params.days = days;
  
  const response = await eventsApi.get(`/v1/masjid/${masjidId}/events`, { params });
  return response.data;
};

/**
 * Get events for an organization (admin view)
 * @param {string} orgId - Organization ID
 * @param {object} options - Query options
 */
export const getOrgEvents = async (orgId, options = {}) => {
  const { includePast, days } = options;
  const params = {};
  if (includePast != null) params.includePast = includePast;
  if (days != null) params.days = days;
  
  const response = await eventsApi.get(`/v1/org/${orgId}/events`, { params });
  return response.data;
};

/**
 * Fetch an event by its share code (public, no auth required)
 * @param {string} shareCode - The 8-character share code
 */
export const getEventByShareCode = async (shareCode) => {
  const response = await publicEventsApi.get(`/v1/events/share/${shareCode}`);
  return response.data;
};

/**
 * Fetch an event by its ID (public endpoint, auth optional)
 * @param {string} eventId - The event ID
 */
export const getEventById = async (eventId) => {
  const response = await eventsApi.get(`/v1/public/events/${eventId}`);
  return response.data.event;
};

// ============================================================================
// RSVP APIs (User - Auth Required)
// ============================================================================

/**
 * Create or update RSVP for an event
 * @param {string} eventId - Event ID
 * @param {string} status - "going" | "interested" | "not_going"
 * @param {string} recurrenceInstanceId - Optional instance ID for recurring events (YYYY-MM-DD)
 */
export const createRsvp = async (eventId, status, recurrenceInstanceId = null) => {
  const data = { status };
  if (recurrenceInstanceId) data.recurrenceInstanceId = recurrenceInstanceId;
  const response = await eventsApi.post(`/v1/events/${eventId}/rsvp`, data);
  return response.data;
};

/**
 * Get user's RSVP for an event
 * @param {string} eventId - Event ID
 * @param {string} recurrenceInstanceId - Optional instance ID for recurring events
 */
export const getRsvp = async (eventId, recurrenceInstanceId = null) => {
  const params = {};
  if (recurrenceInstanceId) params.recurrenceInstanceId = recurrenceInstanceId;
  const response = await eventsApi.get(`/v1/events/${eventId}/rsvp`, { params });
  return response.data;
};

/**
 * Cancel RSVP for an event
 * @param {string} eventId - Event ID
 * @param {string} recurrenceInstanceId - Optional instance ID for recurring events
 */
export const cancelRsvp = async (eventId, recurrenceInstanceId = null) => {
  const params = {};
  if (recurrenceInstanceId) params.recurrenceInstanceId = recurrenceInstanceId;
  const response = await eventsApi.delete(`/v1/events/${eventId}/rsvp`, { params });
  return response.data;
};

// ============================================================================
// Registration APIs (User - Auth Required)
// ============================================================================

/**
 * Register for an event
 * @param {string} eventId - Event ID
 * @param {object} registrationData - Registration details
 * @param {string} registrationData.ticketTypeId - Selected ticket type ID
 * @param {number} registrationData.quantity - Number of tickets (default 1)
 * @param {string} registrationData.sessionId - Optional session ID
 * @param {string} registrationData.promoCode - Optional promo code
 * @param {object} registrationData.registrantInfo - Registrant details (name, email, phone, etc.)
 * @param {object} registrationData.customFieldResponses - Responses to custom fields
 * @param {boolean} registrationData.waiverAccepted - Whether waiver was accepted
 * @param {string[]} registrationData.childIds - IDs of children to register
 * @param {string} registrationData.recurrenceInstanceId - For recurring events
 */
export const registerForEvent = async (eventId, registrationData) => {
  const response = await eventsApi.post(`/v1/events/${eventId}/register`, registrationData);
  return response.data;
};

/**
 * Get user's registration for an event
 * @param {string} eventId - Event ID
 * @param {string} recurrenceInstanceId - Optional instance ID for recurring events
 */
export const getRegistration = async (eventId, recurrenceInstanceId = null) => {
  const params = {};
  if (recurrenceInstanceId) params.recurrenceInstanceId = recurrenceInstanceId;
  const response = await eventsApi.get(`/v1/events/${eventId}/registration`, { params });
  return response.data;
};

/**
 * Cancel registration for an event
 * @param {string} eventId - Event ID
 * @param {string} recurrenceInstanceId - Optional instance ID for recurring events
 */
export const cancelRegistration = async (eventId, recurrenceInstanceId = null) => {
  const params = {};
  if (recurrenceInstanceId) params.recurrenceInstanceId = recurrenceInstanceId;
  const response = await eventsApi.delete(`/v1/events/${eventId}/registration`, { params });
  return response.data;
};

/**
 * Validate a promo code for an event
 * @param {string} eventId - Event ID
 * @param {string} promoCode - Promo code to validate
 * @param {string} ticketTypeId - Optional ticket type ID to check applicability
 */
export const validatePromoCode = async (eventId, promoCode, ticketTypeId = null) => {
  const data = { promoCode };
  if (ticketTypeId) data.ticketTypeId = ticketTypeId;
  const response = await eventsApi.post(`/v1/events/${eventId}/promo/validate`, data);
  return response.data;
};

// ============================================================================
// Registration Management APIs (Admin - Auth Required)
// ============================================================================

/**
 * Get all registrations for an event (admin only)
 * @param {string} eventId - Event ID
 * @param {object} options - Query options
 * @param {string} options.status - Filter by status (pending, approved, rejected, waitlist, cancelled)
 * @param {string} options.recurrenceInstanceId - For recurring events
 * @param {number} options.limit - Max results
 * @param {string} options.cursor - Pagination cursor
 */
export const getEventRegistrations = async (eventId, options = {}) => {
  const { status, recurrenceInstanceId, limit, cursor } = options;
  const params = {};
  if (status) params.status = status;
  if (recurrenceInstanceId) params.recurrenceInstanceId = recurrenceInstanceId;
  if (limit) params.limit = limit;
  if (cursor) params.cursor = cursor;
  
  const response = await eventsApi.get(`/v1/events/${eventId}/registrations`, { params });
  return response.data;
};

/**
 * Get registration statistics for an event (admin only)
 * @param {string} eventId - Event ID
 * @param {string} recurrenceInstanceId - Optional instance ID for recurring events
 */
export const getEventRegistrationStats = async (eventId, recurrenceInstanceId = null) => {
  const params = {};
  if (recurrenceInstanceId) params.recurrenceInstanceId = recurrenceInstanceId;
  const response = await eventsApi.get(`/v1/events/${eventId}/registrations/stats`, { params });
  return response.data;
};

/**
 * Approve or reject a registration (admin only)
 * @param {string} eventId - Event ID
 * @param {string} registrationId - Registration ID
 * @param {string} action - "approve" | "reject"
 * @param {string} reason - Optional reason for rejection
 */
export const updateRegistrationStatus = async (eventId, registrationId, action, reason = null) => {
  const data = { action };
  if (reason) data.reason = reason;
  const response = await eventsApi.post(`/v1/events/${eventId}/registrations/${registrationId}/status`, data);
  return response.data;
};

/**
 * Check in a registrant (admin only)
 * @param {string} eventId - Event ID
 * @param {string} registrationId - Registration ID
 * @param {string} recurrenceInstanceId - Optional instance ID for recurring events
 */
export const checkInRegistration = async (eventId, registrationId, recurrenceInstanceId = null) => {
  const data = {};
  if (recurrenceInstanceId) data.recurrenceInstanceId = recurrenceInstanceId;
  const response = await eventsApi.post(`/v1/events/${eventId}/registrations/${registrationId}/checkin`, data);
  return response.data;
};

/**
 * Get check-in history for an event (admin only)
 * @param {string} eventId - Event ID
 * @param {string} recurrenceInstanceId - Optional instance ID for recurring events
 */
export const getEventCheckIns = async (eventId, recurrenceInstanceId = null) => {
  const params = {};
  if (recurrenceInstanceId) params.recurrenceInstanceId = recurrenceInstanceId;
  const response = await eventsApi.get(`/v1/events/${eventId}/checkins`, { params });
  return response.data;
};

// ============================================================================
// Event Flyer APIs (Admin - Auth Required)
// ============================================================================

/**
 * Upload event flyer
 * @param {string} eventId - Event ID
 * @param {File} file - Image file to upload
 */
export const uploadEventFlyer = async (eventId, file) => {
  const formData = new FormData();
  formData.append("flyer", file, file.name);
  
  const response = await eventsApi.post(`/v1/events/${eventId}/flyer`, formData, {
    timeout: 120000, // 2 minutes for uploads
  });
  return response.data;
};

/**
 * Delete event flyer
 * @param {string} eventId - Event ID
 */
export const deleteEventFlyer = async (eventId) => {
  const response = await eventsApi.delete(`/v1/events/${eventId}/flyer`);
  return response.data;
};

// ============================================================================
// Share Code APIs
// ============================================================================

/**
 * Get or generate share code for an event
 * @param {string} eventId - Event ID
 */
export const getEventShareCode = async (eventId) => {
  const response = await eventsApi.get(`/v1/events/${eventId}/share-code`);
  return response.data;
};

export default api;


